"use client";

import { type AriaTextFieldOptions, useTextField } from "@react-aria/textfield";
import { useObjectRef } from "@react-aria/utils";
import type { VariantProps } from "class-variance-authority";
import { type ChangeEvent, type ReactNode, type Ref, useEffect } from "react";
import { NumericFormat, type NumericFormatProps } from "react-number-format";

import { cn } from "../../../utils";
import { useTextFieldRootContext } from "../context";
import { inputContainerStyles, inputStyles } from "../style";
import useInputNumberValidation from "./useInputNumberValidation";

export type TextFieldInputNumberProps = {
  className?: string;
  containerClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  shouldAutoResize?: boolean;
  min?: string | number;
  max?: string | number;
  ref?: Ref<HTMLInputElement>;
  isLoading?: boolean;
} & Omit<NumericFormatProps, "prefix" | "suffix" | "customInput"> &
  Omit<AriaTextFieldOptions<"input">, "inputElementType" | "isDisabled" | "onChange" | "prefix" | "suffix" | "value"> &
  VariantProps<typeof inputStyles> &
  VariantProps<typeof inputContainerStyles>;

const InputNumber = ({
  ref: forwardedRef,
  className,
  containerClassName,
  prefix,
  suffix,
  disabled,
  shouldAutoResize,
  variant = "basic",
  onChange,
  isAllowed,
  isInvalid: isInvalidTextField,
  min,
  max,
  isLoading,
  ...props
}: TextFieldInputNumberProps) => {
  const ref = useObjectRef(forwardedRef);
  const { setTextField } = useTextFieldRootContext<"input">();

  const textField = useTextField(
    {
      ...props,
      value: props.value ? String(props.value) : undefined,
      isDisabled: disabled,
      inputElementType: "input",
      // https://github.com/adobe/react-spectrum/issues/1860#issuecomment-849833808
      onChange: (value: string) => {
        const separatorRegex =
          typeof props.thousandSeparator === "string" ? new RegExp(props.thousandSeparator, "g") : /,/g;

        // These values are not valid numbers, so we need to return the value as is
        const parsedValue = !["", "-", "-0", "-0."].includes(value) ? Number(value.replace(separatorRegex, "")) : value;

        onChange?.({
          target: {
            value: parsedValue,
            name: props.name,
          },
        } as ChangeEvent<HTMLInputElement>);
      },
      label: true,
      isInvalid: isInvalidTextField,
    },
    ref
  );

  const { inputProps, isInvalid } = textField;

  useEffect(() => {
    setTextField(textField);
  }, [isInvalid]);

  useEffect(() => {
    if (!shouldAutoResize) {
      return;
    }

    const resizeInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      target.style.width = "0px";
      target.style.width = `${target.scrollWidth}px`;
    };
    const inputElement = ref.current;

    if (inputElement) {
      inputElement.addEventListener("input", resizeInput);
      resizeInput({ target: inputElement } as unknown as Event);
    }
    return () => {
      inputElement?.removeEventListener("input", resizeInput);
    };
  }, [shouldAutoResize, ref]);

  const handleFocus = () => ref.current?.focus();

  useEffect(() => {
    // reset value
    if (props.value === undefined && ref.current) {
      ref.current.value = "";
    }
  }, [props.value, ref]);

  const validate = useInputNumberValidation({ min, max, isAllowed });

  return (
    <>
      <style>
        {`input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          input[type=number] {
            -moz-appearance: textfield;
          }`}
      </style>
      <div
        className={cn(inputContainerStyles({ variant }), containerClassName)}
        data-invalid={isInvalid}
        data-disabled={inputProps.disabled}
        role="button"
        tabIndex={-1}
        onKeyUp={() => null}
        onClick={handleFocus}
      >
        {prefix}

        <NumericFormat
          {...props}
          {...inputProps}
          defaultValue={inputProps.defaultValue ? String(inputProps.defaultValue) : undefined}
          value={props.value}
          className={cn(inputStyles({ variant }), className)}
          type={["text", "tel", "password"].includes(inputProps.type as string) ? props.type : undefined}
          isAllowed={validate}
          getInputRef={ref}
        />

        {suffix}
      </div>
    </>
  );
};

InputNumber.displayName = "InputNumber";

export default InputNumber;
